import {
  Grid2 as Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";

export const SalesUsers = ({ title, staffRows, setSalesData }) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [emailError, setEmailError] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [values, setValues] = useState({
    fullName: "",
    email: "",
  });

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
      }
    }, 3000);

    return () => {
      clearInterval(clearTextInterval); // Clear the interval when the component unmounts
    };
  }, [errorText]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || email.trim() === "") {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Invalid email format";
    }
    return "";
  };

  const validateFullName = (fullName) => {
    if (!fullName || fullName.trim() === "") {
      return "Full name is required";
    }

    const nameParts = fullName
      .trim()
      .split(/\s+/)
      .filter((part) => part.length > 0);

    if (nameParts.length < 2) {
      return "Full name must have at least 2 parts (e.g., 'John Doe')";
    }

    // Check if each part is at least 2 characters long
    const invalidParts = nameParts.filter((part) => part.length < 2);
    if (invalidParts.length > 0) {
      return "Each name part must be at least 2 characters long";
    }

    return "";
  };

  const handleAddUser = async () => {
    // Validate email and full name for add operations
    const emailValidationError = validateEmail(values?.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }
    setEmailError("");

    const fullNameValidationError = validateFullName(values?.fullName);
    if (fullNameValidationError) {
      setFullNameError(fullNameValidationError);
      return;
    }
    setFullNameError("");

    try {
      setAddLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users/manage`,
        {
          values: values,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users`,
        { currentSession: currentSession },
        { withCredentials: true }
      );

      setSalesData(res?.data?.data);

      if (resp?.data?.message) {
        setErrorText(resp?.data?.message);
      }

      setAddLoading(false);
      setValues({ fullName: "", email: "" });
      setEmailError("");
      setFullNameError("");
    } catch (e) {
      setErrorText(e.response?.data?.message || "An error occurred");
      setAddLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/user/delete`,
        {
          userEmail: userToDelete.email,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users`,
        { currentSession: currentSession },
        { withCredentials: true }
      );

      setSalesData(res?.data?.data);

      if (resp?.data?.message) {
        setErrorText(resp?.data?.message);
      }

      setDeleteLoading(false);
      setDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (e) {
      setErrorText(e.response?.data?.message || "An error occurred");
      setDeleteLoading(false);
    } finally {
      setDeleteLoading(false);
      setUserToDelete(null);
      setTimeout(() => {
        setErrorText("");
      }, 5000);
    }
  };

  const staffColumnsSmall = [
    {
      field: "name",
      headerName: title,
      flex: 1,
      editable: false,
      renderCell: (params) => {
        return (
          <>
            <Box sx={{ display: "flex", width: "100%", pr: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  // borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                  pb: 0.2,
                  pl: 1,
                  minWidth: "180px",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.90rem",
                    color: "rgba(0, 0, 0, 0.8)",
                    fontWeight: 500,
                  }}
                >
                  {params?.value}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.85rem",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    color: "rgba(0,0,0,0.62)",
                  }}
                >
                  {params.row?.email}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.85rem",
                    color: "rgba(0,0,0,0.62)",
                    whiteSpace: "normal",
                  }}
                >
                  {params.row?.added_by} • {params.row?.date}
                </Typography>
                <Chip
                  label={params.row?.status === 0 ? "Invited" : "Active"}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "12px",
                    width: "max-content",
                    marginTop: "2px",
                    backgroundColor:
                      params.row?.status === 0 ? "#ff980040" : "#4CAF5040",
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  pb: 0.2,
                  pr: 1,
                }}
              >
                <Tooltip title="Delete User">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(params.row);
                    }}
                    sx={{ color: "error.main" }}
                    size="small"
                  >
                    <DeleteIcon sx={{ fontSize: "1.3rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </>
        );
      },
    },
  ];

  const staffColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 0.5,
      width: "100%",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      width: "100%",
    },
    {
      field: "added_by",
      headerName: "Added By",
      flex: 1,
      width: "100%",
    },
    {
      field: "date",
      headerName: "Date",
      flex: 0.5,
      width: "100%",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      width: "100%",
      valueGetter: (value) => {
        return value === 0 ? "Invited" : "Active";
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.3,
      width: "100%",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Tooltip title="Delete User">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(params.row);
              }}
              sx={{ color: "error.main" }}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Card sx={{ minHeight: "35rem" }}>
      <CardHeader title={title} sx={{ px: mobileSize ? "12px" : "24px" }} />
      <CardContent
        sx={{ py: "0px !important", px: mobileSize ? "12px" : "24px" }}
      >
        <DataGrid
          sx={{
            "& .wrapHeader .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            },
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader": {
              ".MuiDataGrid-menuIcon": {
                display: "none",
                pointerEvents: "none",
              },
              cursor: "default",
              paddingLeft: 1,
              paddingRight: 1,
              margin: 0,
              backgroundColor: "#333",
              color: "#fff",
              borderBottom: "1px solid #ccc",
            },
            // Allow sortable column headers to remain interactive
            "& .MuiDataGrid-columnHeader--sortable": {
              pointerEvents: "auto",
              cursor: "pointer",
            },
            "& .MuiDataGrid-columnHeaders": {
              padding: 0, // Adjust as needed
              margin: 0, // Adjust as needed
            },
            "& .tablet-empty-header .MuiDataGrid-columnHeaderTitleContainer": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeadersInner": {
              width: "100%", // Adjust as needed
              padding: 0,
            },
            border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid

            "& .MuiDataGrid-cell": {
              borderColor: tabletSize ? "rgba(0, 0, 0, 0.1)" : "#333333",
              p: tabletSize ? 0 : undefined,
            },
            minHeight: "24rem",
          }}
          rows={staffRows}
          columns={tabletSize ? staffColumnsSmall : staffColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowHeight={tabletSize ? 110 : 36}
        />
        <Grid container spacing={2} alignItems="center" sx={{ mt: "1rem" }}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                backgroundColor: "primary.main",
                padding: "1rem",
              }}
            >
              <Typography
                variant="body1"
                color="white"
                fontWeight="bold"
                textAlign="center"
                sx={{ minHeight: "1.5rem" }}
              >
                Add New User
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              disabled={addLoading}
              label={"Full Name"}
              variant="filled"
              fullWidth
              value={values?.fullName || ""}
              onChange={(e) => {
                setValues({ ...values, fullName: e.target.value });
                if (fullNameError) {
                  setFullNameError("");
                }
              }}
              error={!!fullNameError}
              helperText={fullNameError || " "}
              onBlur={(e) => {
                const validationError = validateFullName(e.target.value);
                setFullNameError(validationError);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              disabled={addLoading}
              label={"Email"}
              variant="filled"
              fullWidth
              value={values?.email || ""}
              onChange={(e) => {
                setValues({ ...values, email: e.target.value });
                if (emailError) {
                  setEmailError("");
                }
              }}
              error={!!emailError}
              helperText={emailError || " "}
              onBlur={(e) => {
                const validationError = validateEmail(e.target.value);
                setEmailError(validationError);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <LoadingButton
              loading={addLoading}
              disabled={!values?.email || !values?.fullName || addLoading}
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                handleAddUser();
              }}
            >
              Add New
            </LoadingButton>
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          color="primary"
          fontWeight="bold"
          textAlign="center"
          sx={{ mt: "1rem", minHeight: "1.5rem" }}
        >
          {errorText}
        </Typography>
      </CardContent>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove sales user:{" "}
            <strong>{userToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteConfirm}
            loading={deleteLoading}
            variant="contained"
            color="error"
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
